import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Code,
  ColorPicker,
  DatePicker,
  Input,
  ScrollArea,
  Slider,
  TimePicker,
  VStack,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

const SCROLLBARS = [
  { variant: "default" as const, label: "scrollbar-default" },
  { variant: "thin" as const, label: "scrollbar-thin" },
  { variant: "none" as const, label: "scrollbar-none" },
];

export function BrowserElementsSection() {
  const [date, setDate] = useState("2026-07-22");
  const [color, setColor] = useState("#635BFF");
  const [time, setTime] = useState("14:30");

  return (
    <Section
      id="browser-elements"
      eyebrow="Fondations"
      title="Éléments natifs"
      description="Sélecteurs faits maison, barres de défilement, sélection de texte et anneaux de focus : ce que le navigateur fournit par défaut, réaccordé au thème."
    >
      <SubSection title="Sélecteurs" hint="Popover sur desktop, BottomSheet sous 640px">
        <Preview
          layout="stack"
          label="pickers.tsx"
          code={`<DatePicker value={date} onChange={setDate} />
<ColorPicker value={color} onChange={setColor} />
<TimePicker value={time} onChange={setTime} />`}
        >
          <div className="grid w-full gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-fg">Date</span>
              <DatePicker value={date} onChange={setDate} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-fg">Couleur</span>
              <ColorPicker value={color} onChange={setColor} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-fg">Heure</span>
              <TimePicker value={time} onChange={setTime} />
            </label>
          </div>
        </Preview>
      </SubSection>

      <SubSection title="Barres de défilement">
        <Preview
          layout="stack"
          label="scroll-area.tsx"
          code={`<ScrollArea scrollbarVariant="thin" maxHeight="max-h-36">…</ScrollArea>`}
        >
          <div className="grid w-full gap-4 sm:grid-cols-3">
            {SCROLLBARS.map(({ variant, label }) => (
              <div key={variant}>
                <p className="mb-2 font-mono text-[11px] text-fg-muted">{label}</p>
                <ScrollArea
                  scrollbarVariant={variant}
                  maxHeight="max-h-36"
                  className="rounded-lg border border-border p-3"
                >
                  <VStack gap={2}>
                    {Array.from({ length: 6 }, (_, index) => (
                      <div key={index} className="rounded bg-surface-sunken p-2 text-xs text-fg">
                        Ligne {index + 1}
                      </div>
                    ))}
                  </VStack>
                </ScrollArea>
              </div>
            ))}
          </div>
        </Preview>
      </SubSection>

      <SubSection
        title="Bords estompés"
        hint="Masque piloté par animation-timeline: scroll() — aucun écouteur scroll"
      >
        <Preview
          layout="stack"
          className="max-w-md"
          label="scroll-fade.tsx"
          code={`<ScrollArea fade maxHeight="max-h-40">…</ScrollArea>

/* Le masque s'ouvre du côté où il reste du contenu, et cette
   ouverture est calculée par le compositeur à partir de la
   position de défilement elle-même. */`}
        >
          <ScrollArea fade maxHeight="max-h-40" className="w-full">
            <VStack gap={2}>
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="rounded bg-surface-sunken px-3 py-2 text-xs text-fg">
                  Élément {index + 1}
                </div>
              ))}
            </VStack>
          </ScrollArea>
        </Preview>
      </SubSection>

      <SubSection title="Sélection de texte">
        <Preview
          layout="stack"
          label="theme.css"
          code={`::selection {
  background-color: var(--color-accent-subtle);
  color: var(--color-accent-fg);
}`}
        >
          <Card variant="raised" className="p-4">
            <CardBody className="p-0">
              <p className="text-sm text-fg">
                Sélectionnez ce texte : <Code>::selection</Code> applique une teinte d'accentuation
                au lieu du bleu brut du système.
              </p>
            </CardBody>
          </Card>
        </Preview>
      </SubSection>

      <SubSection title="Contrôles natifs habillés">
        <Preview
          layout="stack"
          label="native-controls.tsx"
          code={`<Slider defaultValue={65} showValue formatValue={(v) => \`\${v} %\`} />

<Input type="file" />`}
        >
          <div className="w-full max-w-md">
            <Slider defaultValue={65} showValue formatValue={(value) => `${value} %`} />
          </div>
          <Input type="file" className="max-w-md text-xs" />
        </Preview>
      </SubSection>

      <SubSection title="Focus & tactile" hint="Appuyez sur Tab">
        <Preview
          label="focus.tsx"
          code={`/* theme.css */
:focus-visible {
  outline: 2px solid color-mix(in oklch, var(--color-ring) 75%, transparent);
  outline-offset: 1px;
  border-radius: inherit;
}

* { -webkit-tap-highlight-color: transparent; }`}
        >
          <Button variant="outline" size="sm">
            Focus 1
          </Button>
          <Button variant="outline" size="sm">
            Focus 2
          </Button>
          <Badge tone="success">Aucun carré bleu sur iOS / Android</Badge>
        </Preview>
      </SubSection>
    </Section>
  );
}
